import { useBranchMenuGroupMutations } from "@/features/branch-sales-channel/usecases/useBranchMenuGroupMutations";
import { useSalesChannelOptions } from "@/features/branch-sales-channel/usecases/useSalesChannelOptions";
import { useBranchAutocomplete } from "@/features/branch/usecases/useBranchAutocomplete";
import {
	type AutocompleteFetcher,
	Badge,
	Button,
	Can,
	DataTable,
	type DataTableFiltersProp,
	toast,
} from "@k3-tech/react-kit";
import type { ColumnDef } from "@tanstack/react-table";
import { LinkIcon } from "lucide-react";
import { useCallback, useMemo, useRef, useState } from "react";
import {
	type BranchSalesChannelCategoryGroupRow,
	useBranchSalesChannelCategoryGroupList,
} from "../usecases/useBranchSalesChannelCategoryGroupList";
import {
	type BranchBindFormValues,
	BranchMenuGroupBindDialog,
} from "./BranchMenuGroupBindDialog";

export interface BranchSalesChannelCategoryGroupTableProps {
	groupID: string;
	groupName: string;
}

export function BranchSalesChannelCategoryGroupTable({
	groupID,
	groupName,
}: BranchSalesChannelCategoryGroupTableProps) {
	const branches = useBranchSalesChannelCategoryGroupList(groupID);
	const { load: loadBranch } = useBranchAutocomplete();
	const { options: channelOptions, nameByID: channelNameByID } =
		useSalesChannelOptions();
	const mutations = useBranchMenuGroupMutations();
	const [bindOpen, setBindOpen] = useState(false);

	// Stash branchCode/branchName from the fetched options so a bind can send
	// them without parsing the label string.
	const branchRawByID = useRef(
		new Map<string, { branchCode: string; branchName: string }>(),
	);

	const branchFetcher = useCallback<AutocompleteFetcher>(
		async (params) => {
			const result = await loadBranch(
				params.page,
				params.pageSize,
				params.search,
			);
			for (const item of result.items) {
				const raw = item.raw as
					| { branchCode?: string; branchName?: string }
					| undefined;
				branchRawByID.current.set(String(item.value), {
					branchCode: raw?.branchCode ?? "",
					branchName: raw?.branchName ?? item.label,
				});
			}
			return result;
		},
		[loadBranch],
	);

	const submitBind = async (values: BranchBindFormValues) => {
		const raw = branchRawByID.current.get(values.branchID);
		const result = await mutations.create({
			branchID: values.branchID,
			branchCode: raw?.branchCode ?? "",
			branchName: raw?.branchName ?? "",
			salesChannelID: values.salesChannelID,
			salesChannelCategoryGroupID: groupID,
			salesChannelCategoryGroupName: groupName,
		});
		if (result.error) {
			toast.error(result.error.message);
			throw result.error;
		}
		toast.success("Branch bound");
		setBindOpen(false);
		branches.load();
	};

	const columns = useMemo<ColumnDef<BranchSalesChannelCategoryGroupRow>[]>(
		() => [
			{ header: "Branch Code", accessorKey: "branchCode" },
			{ header: "Branch Name", accessorKey: "branchName" },
			{
				header: "Channel",
				accessorKey: "salesChannelID",
				cell: ({ row }) => (
					<Badge variant="secondary">
						{channelNameByID.get(row.original.salesChannelID) ??
							row.original.salesChannelID}
					</Badge>
				),
			},
		],
		[channelNameByID],
	);

	const formFilters = useMemo<DataTableFiltersProp>(
		() => [
			{
				variant: "plain",
				layout: "flex",
				flex: { direction: "row", wrap: true, gap: "gap-3" },
				fields: [
					{
						name: "branchID",
						label: " ",
						type: "autocomplete",
						autocompleteMode: "server",
						placeholder: "Search branch",
						options: [],
						fetcher: branchFetcher,
					},
					{
						name: "salesChannelID",
						label: " ",
						type: "select",
						className: "w-full",
						placeholder: "Channel",
						options: [{ label: "All", value: null }, ...channelOptions],
					},
				],
			},
		],
		[channelOptions, branchFetcher],
	);

	const onFormFilterChange = (values: Record<string, unknown>) => {
		branches.setFilters({
			branchID: values.branchID || null,
			salesChannelID: values.salesChannelID || null,
		});
	};

	return (
		<>
			<div className="mb-3 flex justify-end">
				<Can permissions="sales_channel_category_groups.create">
					<Button size="sm" variant="outline" onClick={() => setBindOpen(true)}>
						<LinkIcon className="h-3.5 w-3.5" />
						Bind Branch
					</Button>
				</Can>
			</div>
			<DataTable<BranchSalesChannelCategoryGroupRow, unknown>
				columns={columns}
				data={branches.items}
				filterWrapper="none"
				filterShowActionsSeparator={false}
				loading={branches.fetching}
				formFilters={formFilters}
				onFormFilterChange={onFormFilterChange}
				pagination={branches.rowCount > 0}
				paginationState={branches.paginationState}
				onPaginationChange={branches.setPagination}
				rowCount={branches.rowCount}
				showStandardActions
				onRefresh={branches.load}
			/>

			<BranchMenuGroupBindDialog
				open={bindOpen}
				onOpenChange={setBindOpen}
				branchFetcher={branchFetcher}
				channelOptions={channelOptions}
				onSubmit={submitBind}
			/>
		</>
	);
}
